import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Lifecycle of a debounced save.
 *
 * - `pending` — the value changed and a save is scheduled (nothing sent yet)
 * - `saving` — a request is in flight (or queued behind one)
 * - `saved` / `error` — terminal feedback, reverts to `idle` after `feedbackMs`
 *
 * For "are there edits the server has not accepted?", read `hasUnsavedChanges`
 * rather than deriving it from this: a failed save returns to `idle` while the
 * edit is still unsaved.
 */
export type AutoSaveState = 'idle' | 'pending' | 'saving' | 'saved' | 'error'

/**
 * The debounce policy for auto-saving UI. Pick the tier that matches what a
 * save costs; do not invent new numbers at call sites.
 */
export const AUTO_SAVE_DEBOUNCE_MS = {
  /** Cheap single-field writes where the user expects near-instant persistence. */
  fast: 500,
  /** Long-form prose — saving on every keystroke pause is wasteful, but a lost second hurts. */
  medium: 1000,
  /** Large or expensive payloads (code drafts, whole-record forms). */
  slow: 2000,
} as const

/** How long a `saved` / `error` indicator stays on screen before reverting to `idle`. */
export const AUTO_SAVE_FEEDBACK_MS = 2000

interface UseAutoSaveOptions<T> {
  /**
   * Fallback payload: what gets saved when `onChange()` / `flush()` are called
   * with no argument, and the initial baseline for `skipUnchanged`. Read at fire
   * time, so the newest rendered value wins. Omit it when every call supplies
   * its own payload (toggles, dropdowns, one-off commands).
   */
  value?: T
  /**
   * Persists the value. Return the promise so the hook can serialize saves and
   * see failures — a fire-and-forget call (tRPC `mutate`) opts out of both.
   */
  onSave: (value: T) => unknown
  /** Delay before an `onChange` reaches `onSave`. Defaults to `AUTO_SAVE_DEBOUNCE_MS.fast`. */
  debounceMs?: number
  /** How long `saved` / `error` stay visible. Defaults to `AUTO_SAVE_FEEDBACK_MS`. */
  feedbackMs?: number
  /** Normalizes the value before it is saved (e.g. formatting). */
  transform?: (value: T) => T
  /** Skip saving a value equal (`Object.is`) to the last successfully saved one. */
  skipUnchanged?: boolean
  /**
   * Checked when the save is about to be sent: return false to drop it (e.g. a
   * record that is not yet complete enough to persist). Dropping leaves no
   * "saved" feedback behind, because nothing was saved.
   */
  canSave?: (value: T) => boolean
  /**
   * Called after a save succeeds, with the transformed value and the value as
   * requested. Skipped for a superseded save, so anything the app must apply
   * exactly once per response (an id the server just assigned) belongs in
   * `onSave` instead.
   */
  onSaved?: (savedValue: T, requestedValue: T) => void
  /** Called after a save fails, with the error and the value that failed to save. */
  onError?: (error: unknown, attemptedValue: T) => void
  /** Prefix for the `console.error` emitted when a save throws. */
  logLabel?: string
  /**
   * Send a scheduled-but-unsent save on unmount instead of dropping it. Off by
   * default: only opt in where the edit outlives the component (collapsible
   * panels, route changes), never where unmount means the record is gone.
   */
  flushOnUnmount?: boolean
}

interface UseAutoSaveResult<T> {
  /** Current save state, for rendering the indicator. */
  state: AutoSaveState
  /**
   * Whether edits exist that the server has not accepted — scheduled, in
   * flight, or failed. Stays true after a failure, unlike `state`.
   */
  hasUnsavedChanges: boolean
  /** Schedule a debounced save. Pass the new value, or omit it to save `value` when the timer fires. */
  onChange: (next?: T) => void
  /** Save immediately, cancelling any scheduled save. Pass a value to save that instead of the pending one. */
  flush: (next?: T) => void
  /** Drop a scheduled save. An in-flight save is left alone — it cannot be recalled. */
  cancel: () => void
}

function clearTimer(ref: { current: ReturnType<typeof setTimeout> | null }) {
  if (ref.current !== null) {
    clearTimeout(ref.current)
    ref.current = null
  }
}

/**
 * Debounced save with status feedback — the one state machine behind every
 * auto-saving control in the app.
 *
 * Guarantees every caller gets for free:
 * - **Serialized requests.** A save scheduled while another is in flight waits
 *   for it to settle, so the server can never apply an older payload after a
 *   newer one.
 * - **Stale-response rejection.** Only the newest save may move the state, so a
 *   slow older response cannot flip the UI to "saved" for a value the user has
 *   already moved past.
 * - **No feedback lies.** A save that resolves while a newer edit is already
 *   queued returns to `pending`, not `saved`; failures show `error` rather than
 *   collapsing to `idle`, which renders identically to success.
 * - **Timer cleanup on unmount**, optionally flushing the pending save first.
 *
 * `T` may not include `undefined`: a missing `onChange` / `flush` argument means
 * "use `value`", so an `undefined` payload would be indistinguishable from it.
 */
export function useAutoSave<T extends NonNullable<unknown> | null>(
  options: UseAutoSaveOptions<T>,
): UseAutoSaveResult<T> {
  const [state, setState] = useState<AutoSaveState>('idle')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Latest options, read at fire time so the returned callbacks stay stable and
  // no save runs against a stale closure. Assigned during render (as
  // session-context.tsx does for its own mutation refs) rather than in an
  // effect, so a `flush()` in the same commit as a state update still sees that
  // update's value instead of the previous render's.
  const optionsRef = useRef(options)
  optionsRef.current = options

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Explicit payload for the scheduled save; null means "read `value` instead".
  const pendingRef = useRef<{ value: T } | null>(null)
  // Whether a save is scheduled but not yet sent. Distinct from the timer ref so
  // a settling save can tell that a newer edit is already waiting.
  const isScheduledRef = useRef(false)
  const lastSavedRef = useRef<T | undefined>(options.value)
  // The most recent request; the next one chains onto it rather than racing it.
  const inFlightRef = useRef<Promise<unknown> | null>(null)
  // Monotonic id of the newest save. A save whose captured id no longer matches
  // has been superseded and must not touch state.
  const saveIdRef = useRef(0)
  // A failure withheld because a newer edit was already scheduled. Held rather
  // than dropped: if that edit never reaches the server — `canSave` blocks it —
  // this is the only surviving record that the save failed.
  const heldErrorRef = useRef<{ error: unknown; value: T } | null>(null)
  const isMountedRef = useRef(true)

  const takePending = useCallback((): T => {
    const pending = pendingRef.current
    pendingRef.current = null
    // `value` is what a bare onChange()/flush() means, so callers that use the
    // bare form must supply it — a pairing the option types cannot express.
    return pending ? pending.value : (optionsRef.current.value as T)
  }, [])

  /** Show a terminal indicator, then fall back to pending/idle. */
  const showTerminal = useCallback((next: 'saved' | 'error') => {
    clearTimer(feedbackTimerRef)
    setState(next)
    feedbackTimerRef.current = setTimeout(() => {
      feedbackTimerRef.current = null
      if (!isMountedRef.current) return
      setState(isScheduledRef.current ? 'pending' : 'idle')
    }, optionsRef.current.feedbackMs ?? AUTO_SAVE_FEEDBACK_MS)
  }, [])

  /**
   * Resolve what would actually be sent. `blocked` still counts as unsaved work
   * (the record just is not persistable yet); `unchanged` does not.
   */
  const prepare = useCallback(
    (
      requestedValue: T,
    ):
      | { status: 'send'; value: T }
      | { status: 'blocked' }
      | { status: 'unchanged' } => {
      const { transform, canSave, skipUnchanged } = optionsRef.current
      if (canSave && !canSave(requestedValue)) return { status: 'blocked' }
      // `lastSavedRef` only advances when a save resolves, so while a request
      // is in flight it names a value the server may already have moved past.
      // Skipping against it there drops a revert to the persisted value and
      // leaves the in-flight payload as the server's final state.
      if (
        skipUnchanged &&
        inFlightRef.current === null &&
        Object.is(requestedValue, lastSavedRef.current)
      ) {
        return { status: 'unchanged' }
      }
      return {
        status: 'send',
        value: transform ? transform(requestedValue) : requestedValue,
      }
    },
    [],
  )

  /** Send one save, queued behind any request still in flight. */
  const dispatch = useCallback((valueToSave: T): Promise<unknown> => {
    const { onSave } = optionsRef.current
    const previous = inFlightRef.current
    // With nothing in flight, onSave is called synchronously, so callers that
    // fire and forget (tRPC `mutate`) still see the request go out this tick.
    const attempt = previous
      ? previous.catch(() => {}).then(() => onSave(valueToSave))
      : (async () => onSave(valueToSave))()
    inFlightRef.current = attempt
    return attempt
  }, [])

  const runSave = useCallback(
    async (requestedValue: T) => {
      if (!isMountedRef.current) return

      const prepared = prepare(requestedValue)
      if (prepared.status !== 'send') {
        // A blocked edit is the end of the line for any failure withheld from
        // an earlier attempt: nothing is going out, so no later settle will
        // report it. Surface it now rather than let the save fail silently.
        // Only once the queue is actually idle — a request still in flight
        // will report or supersede it on its own.
        const held = prepared.status === 'blocked' ? heldErrorRef.current : null
        if (held && inFlightRef.current === null) {
          heldErrorRef.current = null
          optionsRef.current.onError?.(held.error, held.value)
          showTerminal('error')
        } else {
          setState((prev) => (prev === 'saving' ? 'saving' : 'idle'))
        }
        if (prepared.status === 'unchanged') {
          // This scheduled edit resolved to a no-op: `unchanged` is only
          // reachable with nothing in flight, so the server already holds the
          // value and there is no outstanding work left to supersede anything.
          // Carrying the marker forward is what left a bare `onChange()` —
          // which never takes the fast path in `onChange`, because it has no
          // argument to compare — reporting dirty forever.
          isScheduledRef.current = false
          setHasUnsavedChanges(false)
          // Reachable only with nothing in flight and the value equal to
          // `lastSavedRef`, which advances on success alone — the server holds
          // this value, so any earlier failure is moot.
          heldErrorRef.current = null
        }
        // `blocked` deliberately leaves isScheduledRef alone. A newer edit
        // that cannot be sent yet (canSave said no) is still newer work the
        // server does not have, so it must keep superseding any older request
        // already in flight — clearing the flag there would let that older
        // response settle as current and report "saved" for a value the user
        // replaced.
        return
      }

      // The scheduled edit is now genuinely being sent; only here does it stop
      // being outstanding work. Cleared after prepare, never before it.
      isScheduledRef.current = false

      const { onSaved, onError, logLabel } = optionsRef.current
      const valueToSave = prepared.value
      const saveId = ++saveIdRef.current

      // Cancel a pending idle-reset from the previous save so it cannot fire
      // mid-save and clear this save's feedback early.
      clearTimer(feedbackTimerRef)
      setState('saving')

      const settle = (result: 'saved' | 'error') => {
        // A newer edit is already queued, so terminal feedback would describe a
        // value the user has moved past: "saved" would claim their latest text
        // is persisted, and "error" would report a failure for an attempt they
        // have already replaced. Both are the same lie — stay `pending` for the
        // edit that is still coming.
        if (isScheduledRef.current) {
          setState('pending')
          return
        }
        showTerminal(result)
      }

      const attempt = dispatch(valueToSave)

      try {
        await attempt
        if (!isMountedRef.current || saveIdRef.current !== saveId) return
        lastSavedRef.current = valueToSave
        // A newer value reached the server, so an earlier attempt's failure no
        // longer describes anything the user is missing.
        heldErrorRef.current = null
        // `saveIdRef` only advances when a save is *sent*, so it cannot see an
        // edit still sitting in the debounce window. That edit supersedes this
        // response just as much as a dispatched one: firing `onSaved` here
        // would apply this save's (older, possibly transformed) value over
        // what the user has already typed.
        const superseded = isScheduledRef.current
        setHasUnsavedChanges(superseded)
        if (!superseded) onSaved?.(valueToSave, requestedValue)
        settle('saved')
      } catch (error) {
        console.error(`${logLabel ?? 'Auto-save'} save failed:`, error)
        if (!isMountedRef.current || saveIdRef.current !== saveId) return
        // The edit is still only on the client — keep saying so after the
        // error indicator times out.
        setHasUnsavedChanges(true)
        // A failure is staleness-guarded on exactly the same terms as a
        // success: `saveIdRef` cannot see an edit still sitting in the debounce
        // window, so a newer scheduled edit supersedes this rejection too.
        // Firing `onError` here would hand the caller a value the user has
        // already replaced — the mirror of the `onSaved` case above.
        //
        // But only a scheduled edit that is genuinely still coming can speak
        // for this failure. A pending debounce timer will run `runSave` again,
        // which either sends the value (superseding this rejection) or resolves
        // `blocked` and reports it. With no timer left, that run has already
        // happened and found nothing to send: `isScheduledRef` stays set for a
        // blocked edit, so it alone would withhold this failure forever.
        const supersedingSaveStillComing =
          isScheduledRef.current && debounceTimerRef.current !== null
        if (supersedingSaveStillComing) {
          heldErrorRef.current = { error, value: valueToSave }
          setState('pending')
        } else {
          heldErrorRef.current = null
          onError?.(error, valueToSave)
          showTerminal('error')
        }
      } finally {
        if (inFlightRef.current === attempt) {
          inFlightRef.current = null
        }
      }
    },
    [dispatch, prepare, showTerminal],
  )

  const onChange = useCallback(
    (next?: T) => {
      const { debounceMs, skipUnchanged } = optionsRef.current

      clearTimer(debounceTimerRef)
      clearTimer(feedbackTimerRef)

      // Typing back to the persisted value clears the indicator immediately
      // rather than scheduling a save that would be dropped anyway. Only safe
      // with nothing in flight: mid-request the baseline is the *older* value,
      // so this would drop the revert and let the in-flight payload win.
      if (
        skipUnchanged &&
        next !== undefined &&
        inFlightRef.current === null &&
        Object.is(next, lastSavedRef.current)
      ) {
        pendingRef.current = null
        isScheduledRef.current = false
        setHasUnsavedChanges(false)
        setState((prev) => (prev === 'saving' ? 'saving' : 'idle'))
        return
      }

      pendingRef.current = next === undefined ? null : { value: next }
      isScheduledRef.current = true
      setHasUnsavedChanges(true)
      setState((prev) => (prev === 'saving' ? 'saving' : 'pending'))

      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null
        void runSave(takePending())
      }, debounceMs ?? AUTO_SAVE_DEBOUNCE_MS.fast)
    },
    [runSave, takePending],
  )

  const flush = useCallback(
    (next?: T) => {
      clearTimer(debounceTimerRef)
      if (next !== undefined) {
        pendingRef.current = { value: next }
        // An explicitly flushed value is newer work, exactly as `onChange`
        // treats it, and the marker has to be set *before* `prepare` runs: a
        // flush the record is not ready for (`canSave` false) must still
        // supersede an older request already in flight. Without this, that
        // older response settled as current and reported "saved" for a value
        // the server never accepted. `runSave` clears the marker once the
        // value is genuinely sent, and its `unchanged` branch clears it when
        // there turns out to be nothing to send.
        isScheduledRef.current = true
        setHasUnsavedChanges(true)
      }
      void runSave(takePending())
    },
    [runSave, takePending],
  )

  const cancel = useCallback(() => {
    clearTimer(debounceTimerRef)
    pendingRef.current = null
    isScheduledRef.current = false
    heldErrorRef.current = null
    setHasUnsavedChanges(false)
    setState((prev) => (prev === 'pending' ? 'idle' : prev))
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      const wasScheduled = isScheduledRef.current
      clearTimer(debounceTimerRef)
      clearTimer(feedbackTimerRef)
      isScheduledRef.current = false

      const { flushOnUnmount, logLabel } = optionsRef.current
      if (!wasScheduled || !flushOnUnmount) {
        pendingRef.current = null
        return
      }

      const prepared = prepare(takePending())
      if (prepared.status !== 'send') return
      // No state to update any more, so this bypasses runSave: it still queues
      // behind an in-flight request, but nothing reports its outcome.
      dispatch(prepared.value).catch((error) => {
        console.error(`${logLabel ?? 'Auto-save'} save failed:`, error)
      })
    }
  }, [dispatch, prepare, takePending])

  return { state, hasUnsavedChanges, onChange, flush, cancel }
}

export type { UseAutoSaveOptions, UseAutoSaveResult }
